import React, { useState } from 'react';
import cx from 'classnames';
import { useHistory, Link } from 'react-router-dom';
import _orderBy from 'lodash/orderBy';

import {
    DropdownOption,
    DropdownWrapper,
    EntityCard,
    IconUserFolder,
    IconGroupSolid,
} from '@trqk/components';

import { locale, updateAndReplaceURLSearchParams } from '@trqk/utils';
import { useEntities } from '../../../hooks';
import { HASHES, PATHS, URL_PARAMS } from '../../../constants';
import { EntitySelectorPreview } from './EntitySelectorPreview';

const height = 76;
const width = 280;
const expandedHeaderHeight = 40;
const expandedFooterHeight = 60;
const maxListLength = 4;

const allEntitiesIcon = <IconGroupSolid fillColor="white" width={30} />;

const renderAtPaths = [
    PATHS.DETAIL,
    PATHS.DETECTIONS,
    PATHS.GAP_ANALYSIS,
    PATHS.INSIGHTS,
    PATHS.REPORTS,
    PATHS.STATEMENTS,
    PATHS.WORKS,
];

export const EntitySelector = (): JSX.Element | null => {
    const history = useHistory();
    const [renderEntitySelector, setRenderEntitySelector] = useState(false);
    const [renderEntitySelectorPreview, setRenderEntitySelectorPreview] =
        useState(false);

    history.listen(location => {
        // Only render EntitySelector for specific paths
        const shouldRenderEntitySelector = renderAtPaths.some(
            (path: string): boolean => location.pathname.includes(path),
        );

        setRenderEntitySelector(shouldRenderEntitySelector);

        // Only render EntitySelectorPreview for /statements_preview path
        setRenderEntitySelectorPreview(
            location.pathname === PATHS.STATEMENTS_PREVIEW,
        );
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const {
        disableSelection,
        entities,
        loading,
        noSelectedEntity,
        selectedEntity,
        sendEntitySelectionEvent,
        viewAll,
    } = useEntities();

    const sortedEntities = _orderBy<TrqkEntity>(entities, ['name'], ['asc']);
    const nonSelectedEntities = sortedEntities.filter(
        ({ id }: TrqkEntity): boolean => id !== selectedEntity?.id,
    );
    const showScrollArea = entities.length > maxListLength;
    const viewAllOptionHeight = height;
    const scrollAreaHeight = showScrollArea
        ? maxListLength * height
        : entities.length * height + viewAllOptionHeight;
    const listHeight =
        scrollAreaHeight - height + expandedHeaderHeight + expandedFooterHeight;

    if (!renderEntitySelectorPreview && !renderEntitySelector) {
        return null;
    }

    if (renderEntitySelectorPreview) {
        return <EntitySelectorPreview />;
    }

    const currentEntityLabelFullWidth = (
        <EntityCard
            height={height}
            loading={loading}
            name={selectedEntity?.name}
            roleType={selectedEntity?.role_type ?? ('unknown' as ProRoleType)}
            pro={selectedEntity?.pro ?? locale.enUS.UNKNOWN_PRO}
            width={width}
        />
    );

    const allEntitiesLabelFullWidth = (
        <EntityCard
            avatarLabel={allEntitiesIcon}
            height={height}
            name={locale.enUS.ALL_ENTITIES}
            roleType={null}
            width={width}
        />
    );

    const currentEntityLabel = (
        <EntityCard
            height={height}
            loading={loading}
            name={selectedEntity?.name}
            roleType={selectedEntity?.role_type ?? ('unknown' as ProRoleType)}
            pro={selectedEntity?.pro ?? locale.enUS.UNKNOWN_PRO}
            width={width - 20}
        />
    );

    const allEntitiesLabel = (
        <EntityCard
            avatarLabel={allEntitiesIcon}
            height={height}
            name={locale.enUS.ALL_ENTITIES}
            roleType={null}
            width={width - 20}
        />
    );

    let selectedLabel = currentEntityLabel;

    if (viewAll || noSelectedEntity || !selectedEntity) {
        selectedLabel = allEntitiesLabel;
    }

    return (
        <div className="fixed top-0 right-0 z-40 mt-8 mr-8">
            <DropdownWrapper
                boxShadowWhenClosed={true}
                boxShadowWhenOpen={true}
                caretWidth={18}
                disabled={disableSelection}
                height={height}
                leftPadding={false}
                listHeight={listHeight}
                isExpanded={isExpanded}
                selectedLabel={selectedLabel}
                setIsExpanded={setIsExpanded}
                width={width}
                borderConfig="transparent.large"
            >
                <div
                    className="py-4 pl-4 text-sm font-semibold"
                    style={{ height: expandedHeaderHeight, width }}
                >
                    View as:
                </div>
                <div
                    className={cx('overflow-x-hidden', {
                        'overflow-y-auto': showScrollArea,
                    })}
                    style={{ height: scrollAreaHeight }}
                >
                    {/* Card for currently selected entity (or View All if appropriate) */}
                    {selectedEntity && <>{currentEntityLabelFullWidth}</>}
                    {!selectedEntity && <>{allEntitiesLabelFullWidth}</>}
                    {/* Options for each non-selected entity */}
                    {nonSelectedEntities.map(
                        (entity: TrqkEntity): JSX.Element => (
                            <DropdownOption
                                key={entity.id}
                                option={{
                                    label: (
                                        <EntityCard
                                            height={height}
                                            highlightOnHover={true}
                                            name={entity.name}
                                            pro={
                                                entity.pro ??
                                                locale.enUS.UNKNOWN_PRO
                                            }
                                            roleType={entity.role_type}
                                            topBorder={true}
                                            width={width}
                                        />
                                    ),
                                    value: entity.id,
                                }}
                                handleChange={(
                                    option: DropdownOption<Maybe<string>>,
                                ): void => {
                                    // Remove the `statement_ids` URL param so it can be replaced
                                    // by the statement IDs that are selected after the GET_CONTEXT
                                    // event is sent to the statement selection machine
                                    updateAndReplaceURLSearchParams(history, [
                                        {
                                            key: URL_PARAMS.STATEMENT_IDS,
                                            value: null,
                                        },
                                    ]);

                                    // Avoid a lag in closing the entity selector
                                    requestAnimationFrame((): void => {
                                        setIsExpanded(false);

                                        sendEntitySelectionEvent({
                                            type: 'SET_SELECTED_ENTITY',
                                            value: {
                                                selectedEntityId:
                                                    option?.value ?? null,
                                            },
                                        });
                                    });
                                }}
                                height={height}
                                leftPadding={false}
                                underlineOnHover={false}
                            />
                        ),
                    )}
                    {/* option for All Entities */}
                    {selectedEntity !== null && (
                        <DropdownOption
                            disabled={true}
                            option={{
                                label: (
                                    <EntityCard
                                        avatarLabel={allEntitiesIcon}
                                        height={height}
                                        highlightOnHover={false}
                                        name={locale.enUS.ALL_ENTITIES}
                                        roleType={null}
                                        topBorder={true}
                                        width={width}
                                    />
                                ),
                                value: null,
                            }}
                            handleChange={(): void => {
                                sendEntitySelectionEvent({
                                    type: 'SET_SELECTED_ENTITY',
                                    value: {
                                        selectedEntityId: null,
                                    },
                                });

                                setIsExpanded(false);
                            }}
                            height={height}
                            leftPadding={false}
                            underlineOnHover={false}
                        />
                    )}
                </div>
                <div
                    className="flex items-center justify-center border-t border-tertiary-200"
                    style={{ height: expandedFooterHeight, width }}
                >
                    <Link
                        className="underline text-primary-500"
                        onClick={(): void => {
                            setIsExpanded(false);
                        }}
                        to={({ search }) => ({
                            hash: HASHES.SETTINGS.ENTITY_MANAGER,
                            pathname: PATHS.SETTINGS,
                            search,
                            state: {
                                fromLink: true,
                            },
                        })}
                    >
                        <div className="flex items-center justify-center">
                            <IconUserFolder fillColor="primary-500" />
                            <div className="ml-2 text-sm font-semibold underline text-primary-500">
                                Manage Entities
                            </div>
                        </div>
                    </Link>
                </div>
            </DropdownWrapper>
        </div>
    );
};
